import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, ChevronDown, Check, CheckCircle2, X } from 'lucide-react'
import { Vehicle } from '../types'
import { apiClient } from '../config/api'

/** Device health that is not a configured operational alert. */
const EXCLUDED_TYPES = new Set([
  'BATTERY_LOW',
  'SIGNAL_LOW',
  'DEVICE_OFFLINE',
  'GPS_SIGNAL_LOST',
  'LOW_BATTERY_EVENT',
  'TAG_BATTERY_LOW',
  'TAG_MISSING'
])

const TYPE_LABELS: Record<string, string> = {
  GEOFENCE_ENTRY: 'Geofence entry',
  GEOFENCE_EXIT: 'Geofence exit',
  CORRIDOR_DEVIATION: 'Corridor deviation',
  DWELL_TIME: 'Time in zone',
  STOPPED_TOO_LONG: 'Stopped too long',
  SPEED_THRESHOLD: 'Speed limit',
  FUEL_LEVEL: 'Low fuel',
  TEMPERATURE_THRESHOLD: 'Temperature',
  HUMIDITY_THRESHOLD: 'Humidity',
  TAMPER: 'Tamper',
  LOCK_STATE: 'Lock',
  POWER_LOSS: 'Power loss',
  IGNITION_STATE: 'Ignition',
  SOS_PANIC: 'SOS'
}

interface OpenAlert {
  id: number
  vehicle_source: string
  vehicle_id: number
  vehicle_code?: string | null
  alert_type: string
  severity?: string | null
  message?: string | null
  status: string
  can_acknowledge?: boolean
  can_resolve?: boolean
}

interface MapWarningsPanelProps {
  vehicles: Vehicle[]
  closed?: boolean
  onClose?: () => void
  onCount?: (count: number) => void
  onVehicleSelect?: (vehicle: Vehicle) => void
  onOpenDetail?: (alertId: number) => void
}

const typeLabel = (alertType: string) =>
  TYPE_LABELS[alertType] || alertType.replace(/_/g, ' ').toLowerCase().replace(/^\w/, letter => letter.toUpperCase())

const MapWarningsPanel: React.FC<MapWarningsPanelProps> = ({
  vehicles,
  closed = false,
  onClose,
  onCount,
  onVehicleSelect
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [alerts, setAlerts] = useState<OpenAlert[]>([])
  const [loaded, setLoaded] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ x: number, y: number } | null>(null)
  const [size, setSize] = useState<{ width: number, height: number | null }>({ width: 320, height: null })

  const parentBox = () => {
    const parent = panelRef.current?.offsetParent as HTMLElement | null
    return {
      width: parent?.clientWidth || window.innerWidth,
      height: parent?.clientHeight || window.innerHeight
    }
  }

  const currentOrigin = () => {
    if (pos) return pos
    const node = panelRef.current
    const parent = node?.offsetParent as HTMLElement | null
    if (!node || !parent) return { x: 16, y: 64 }
    const nodeRect = node.getBoundingClientRect()
    const parentRect = parent.getBoundingClientRect()
    return { x: nodeRect.left - parentRect.left, y: nodeRect.top - parentRect.top }
  }

  const startDrag = (event: React.PointerEvent) => {
    if ((event.target as HTMLElement).closest('button')) return
    const origin = currentOrigin()
    setPos(origin)
    const startX = event.clientX
    const startY = event.clientY
    let moved = false
    const move = (ev: PointerEvent) => {
      if (Math.abs(ev.clientX - startX) + Math.abs(ev.clientY - startY) > 3) moved = true
      const box = parentBox()
      const width = panelRef.current?.offsetWidth || size.width
      const height = panelRef.current?.offsetHeight || 40
      setPos({
        x: Math.max(0, Math.min(box.width - width, origin.x + ev.clientX - startX)),
        y: Math.max(0, Math.min(box.height - 36, origin.y + ev.clientY - startY))
      })
    }
    const stop = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', stop)
      if (!moved) setOpen(value => !value)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', stop)
  }

  const startResize = (event: React.PointerEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const origin = currentOrigin()
    setPos(origin)
    const startW = panelRef.current?.offsetWidth || size.width
    const startH = panelRef.current?.offsetHeight || 160
    const startX = event.clientX
    const startY = event.clientY
    const move = (ev: PointerEvent) => {
      const box = parentBox()
      setSize({
        width: Math.max(260, Math.min(box.width - origin.x, startW + ev.clientX - startX)),
        height: Math.max(open ? 140 : 40, Math.min(box.height - origin.y, startH + ev.clientY - startY))
      })
    }
    const stop = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', stop)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', stop)
  }

  const load = useCallback(() => {
    apiClient.get('/alerts', { status: 'ACTIVE', limit: 200 })
      .then(response => {
        const rows = Array.isArray(response.data?.alerts) ? response.data.alerts : []
        setAlerts(rows.filter((alert: OpenAlert) => alert?.id && !EXCLUDED_TYPES.has(String(alert.alert_type || '').toUpperCase())))
        setLoaded(true)
      })
      .catch(() => setLoaded(true))
  }, [])

  useEffect(() => { load() }, [load])
  useEffect(() => { onCount?.(alerts.length) }, [alerts.length, onCount])

  const vehicleFor = (alert: OpenAlert) =>
    vehicles.find(vehicle => vehicle.id === `${alert.vehicle_source}-${alert.vehicle_id}`)

  const act = async (alert: OpenAlert, action: 'acknowledge' | 'resolve') => {
    setBusyId(alert.id)
    setError(null)
    try {
      await apiClient.post(`/alerts/${alert.id}/${action}`, {})
      setAlerts(current => current.filter(item => item.id !== alert.id))
    } catch {
      setError(t('warning_action_failed', { defaultValue: 'Could not update this alert' }))
    } finally {
      setBusyId(null)
    }
  }

  if (closed) return null

  return (
    <div
      ref={panelRef}
      style={{
        width: size.width,
        height: size.height ?? undefined,
        left: pos?.x,
        top: pos?.y
      }}
      className={`absolute z-40 flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white/95 shadow-lg dark:border-slate-700 dark:bg-slate-900/95 ${pos ? '' : 'top-16 right-4'}`}
    >
      <div
        className="flex cursor-grab select-none items-center gap-2 px-3 py-2 active:cursor-grabbing"
        onPointerDown={startDrag}
      >
        <span className="text-sm font-bold text-slate-800 dark:text-slate-100">
          {t('warnings', { defaultValue: 'Warnings' })}
        </span>
        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white">
          {alerts.length}
        </span>
        <button
          type="button"
          className="ml-auto rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          onClick={() => setOpen(value => !value)}
          aria-expanded={open}
          aria-label={open ? t('collapse', { defaultValue: 'Collapse' }) : t('expand', { defaultValue: 'Expand' })}
        >
          <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        <button
          type="button"
          className="mr-2 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          onClick={onClose}
          aria-label={t('close', { defaultValue: 'Close' })}
          title={t('close', { defaultValue: 'Close' })}
        >
          <X size={14} />
        </button>
      </div>
      {open && (
        <div className={`flex flex-col gap-1.5 overflow-y-auto px-2 pb-2 ${size.height ? 'min-h-0 flex-1' : 'max-h-[46vh]'}`}>
          {error && <div className="px-1 text-[11px] text-red-600">{error}</div>}
          {loaded && alerts.length === 0 && (
            <div className="px-2 py-3 text-center text-[11px] text-slate-500">
              {t('warnings_none', { defaultValue: 'No open alerts' })}
            </div>
          )}
          {alerts.map(alert => {
            const critical = String(alert.severity || '').toUpperCase() === 'CRITICAL'
            const vehicle = vehicleFor(alert)
            const name = vehicle?.name || alert.vehicle_code || `${alert.vehicle_source} ${alert.vehicle_id}`
            return (
              <div
                key={alert.id}
                className={`rounded-lg border p-2 ${
                  critical
                    ? 'border-red-200 bg-red-50 dark:border-red-900/60 dark:bg-red-950/40'
                    : 'border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/40'
                }`}
              >
                <button
                  type="button"
                  className="flex w-full items-start gap-2 text-left"
                  onClick={() => { if (vehicle) onVehicleSelect?.(vehicle) }}
                >
                  <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${critical ? 'bg-red-500' : 'bg-amber-500'}`}>
                    <AlertTriangle size={11} className="text-white" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-xs font-semibold text-slate-800 dark:text-slate-100">
                      {name} · {typeLabel(alert.alert_type)}
                    </span>
                    {alert.message && (
                      <span className="block text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2">{alert.message}</span>
                    )}
                  </span>
                </button>
                <div className="mt-1.5 flex flex-wrap items-center gap-1 pl-7">
                  {alert.status === 'ACTIVE' && (
                    <button
                      type="button"
                      disabled={busyId === alert.id || alert.can_acknowledge === false}
                      onClick={() => act(alert, 'acknowledge')}
                      className="inline-flex items-center gap-1 rounded border border-amber-300 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 disabled:opacity-40"
                    >
                      <Check size={10} />
                      {t('warning_acknowledge', { defaultValue: 'Acknowledge' })}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={busyId === alert.id || alert.can_resolve === false}
                    onClick={() => act(alert, 'resolve')}
                    className="inline-flex items-center gap-1 rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold text-white disabled:opacity-40"
                  >
                    <CheckCircle2 size={10} />
                    {t('warning_resolve', { defaultValue: 'Resolve' })}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
      <div
        className="absolute bottom-1 right-1 z-10 h-4 w-4 cursor-nwse-resize border-b-2 border-r-2 border-slate-400"
        onPointerDown={startResize}
        title={t('warning_resize', { defaultValue: 'Resize' })}
      />
    </div>
  )
}

export default MapWarningsPanel
